import type { MemoryAccess } from './debug/types';
import { LIMITS, type MemoryPage } from './protocol';

/** Read/write bit mask for only the visible page, including overlapping accesses. */
export function memoryAccessFlags(
	accesses: MemoryAccess[],
	offset: number,
	byteSize: number,
): Uint8Array {
	const flags = new Uint8Array(byteSize);
	for (const access of accesses) {
		const start = Math.max(0, access.address - offset);
		const end = Math.min(byteSize, access.address + access.byteSize - offset);
		const mask = (access.read ? 1 : 0) | (access.write ? 2 : 0);
		for (let index = start; index < end; index++) flags[index] |= mask;
	}
	return flags;
}

export function memoryPage(memory: WebAssembly.Memory, requestedOffset: number): MemoryPage {
	const totalBytes = memory.buffer.byteLength;
	const safeOffset = Number.isFinite(requestedOffset) ? Math.floor(requestedOffset) : 0;
	const offset = Math.max(
		0,
		Math.min(safeOffset, Math.max(0, totalBytes - LIMITS.memoryPageBytes)),
	);
	return {
		offset,
		totalBytes,
		bytes: new Uint8Array(memory.buffer).slice(offset, offset + LIMITS.memoryPageBytes),
	};
}

export function hexAddress(value: number) {
	return `0x${value.toString(16).padStart(8, '0')}`;
}

export function parseAddress(value: string): number | null {
	if (!/^(?:0x[0-9a-f]+|\d+)$/i.test(value.trim())) return null;
	const address = Number(value.trim());
	return Number.isSafeInteger(address) && address >= 0 ? address : null;
}
